import React, { useState } from 'react';
import { authApiClient } from './auth/authApiClient';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import AuthenticatedContent from './components/AuthenticatedContent';
import Login from './components/Login';
import ChunkedUploader from './utils/chunkedUploader';
import { loginRequest } from './auth/authConfig';
import { getApiConfig } from './utils/configLoader';

function App() {
  // MSAL authentication hook
  const { instance, inProgress, accounts } = useMsal();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [mode, setMode] = useState('meeting_summary');
  const [prompt, setPrompt] = useState(
    "Generate a detailed summary of the meeting in the attached video recording, including discussion points and action items with owners"
  );
  const [result, setResult] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [authChecked, setAuthChecked] = useState(false);
  
  const handleVideoSelect = (file) => {
    setSelectedFile(file);
    setFileName(file.name);
    setError('');
  };
  
  const handleProcessVideo = async () => {
    // Validation
    if (!selectedFile) {
      setError('Please select a video file first');
      return;
    }
    
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }
    
    setError('');
    setIsLoading(true);
    setResult('');
    setUploadProgress(0);
    
    try {
      const fileSize = selectedFile.size;
      const fileSizeMB = fileSize / (1024 * 1024);
      let response;
      
      console.log(`Starting upload of ${selectedFile.name} (${fileSizeMB.toFixed(2)} MB)`);
      
      // Always use chunked upload regardless of file size
      console.log('Using chunked upload for all files');
      
      // Create chunked uploader with runtime config
      const apiConfigForUpload = getApiConfig();
      const uploader = new ChunkedUploader(selectedFile, (progress) => {
        console.log(`Upload progress: ${progress}%`);
        setUploadProgress(progress);
      }, apiConfigForUpload.chunkedUploadEndpoint);

      // Variable to store upload result
      let chunkUploadResult;

      try {
        // Start the chunked upload
        console.log('Starting chunked upload process...');
        chunkUploadResult = await uploader.uploadFile();
        console.log('Upload result:', chunkUploadResult);

        if (!chunkUploadResult.success) {
          throw new Error('Chunked upload failed');
        }

        console.log('Chunked upload complete, starting processing');
        console.log('File path:', chunkUploadResult.file_path);
        console.log('Filename:', chunkUploadResult.filename);
        setUploadProgress(100);
      } catch (uploadError) {
        console.error('Chunked upload error:', uploadError);
        throw uploadError;
      }

      // Now process the uploaded file
      const apiConfig = getApiConfig();
      response = await authApiClient.post(
        apiConfig.videoProcessingEndpoint,
        {
          file_path: chunkUploadResult.file_path,
          filename: chunkUploadResult.filename,
          prompt: prompt
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 3600000 // 60 minutes timeout
        }
      );
      
      // Handle success with additional error checking
      if (response && response.data && response.data.success) {
        console.log('Processing successful, setting result');
        setResult(response.data.content);
      } else {
        const errorMessage = response?.data?.message || 'Processing failed';
        console.error('Processing failed:', errorMessage);
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Error processing video:', err);
      setError(
        err.response?.data?.message || 
        'Failed to process the video. Please try again or use a shorter video.'
      );
    } finally {
      setIsLoading(false);
    }
  };
  
  const resetForm = () => {
    setSelectedFile(null);
    setFileName('');
    setResult('');
    setError('');
  };
  
  // Handle login
  const handleLogin = () => {
    instance.loginPopup(loginRequest)
      .then(response => {
        console.log("Login successful", response);
        if (response.account) {
          instance.setActiveAccount(response.account);
        }
      })
      .catch(e => {
        console.error("Login error:", e);
        // Fallback to redirect if popup fails
        if (e.name === "PopupWindowError" || e.name === "BrowserAuthError") {
          console.log("Fallback to redirect login");
          instance.loginRedirect(loginRequest);
        }
      });
  };
  
  // Check for token in URL hash (from implicit flow) and handle auth status
  React.useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Check if we were redirected here due to token expiration
        const redirectedForLogin = sessionStorage.getItem("redirectedForLogin");
        if (redirectedForLogin === "true") {
          console.log("App: App was redirected here due to token expiration, forcing login");
          // Clear the flag immediately to prevent loops
          sessionStorage.removeItem("redirectedForLogin");
          
          // Clear remaining auth data if any
          sessionStorage.removeItem("accessToken");
          sessionStorage.removeItem("idToken");
          sessionStorage.removeItem("tokenExpiresAt");
          sessionStorage.removeItem("loginState");
          sessionStorage.removeItem("loginNonce");
          
          // Force login via the Login component
          setAuthChecked(true);
          return;
        }
        
        // First, check if there's a hash in the URL (from implicit flow redirect)
        console.log("App: Checking for hash in URL and auth status");
        const hash = window.location.hash;
        
        if (hash && (hash.includes("access_token=") || hash.includes("id_token="))) {
          console.log("App: Found token in URL hash, processing...");
          
          // Parse the hash to extract tokens
          const params = new URLSearchParams(hash.substring(1));
          const accessToken = params.get("access_token");
          const idToken = params.get("id_token");
          const state = params.get("state");
          const expiresIn = params.get("expires_in");
          const nonce = params.get("nonce");
          
          // Verify state matches what we stored
          const storedState = sessionStorage.getItem("loginState");
          const storedNonce = sessionStorage.getItem("loginNonce");
          
          if (state && storedState && state === storedState) {
            console.log("App: State validation successful");
            
            // Process the tokens - prioritize ID token for backend compatibility
            if (idToken) {
              console.log("App: Successfully received ID token");
              
              // Store both tokens in sessionStorage (be careful with security)
              sessionStorage.setItem("idToken", idToken);
              
              // For API calls that require it
              if (accessToken) {
                console.log("App: Also received access token");
                sessionStorage.setItem("accessToken", accessToken);
                
                // Calculate expiration time
                if (expiresIn) {
                  const expiresInSeconds = parseInt(expiresIn, 10);
                  const expiresAt = new Date().getTime() + expiresInSeconds * 1000;
                  sessionStorage.setItem("tokenExpiresAt", expiresAt.toString());
                }
              }
              
              // Clear the hash from the URL to prevent issues on refresh
              window.history.replaceState(null, document.title, window.location.pathname);
              
              // Force reload to update auth state
              window.location.reload();
              return;
            } else if (accessToken) {
              console.log("App: Only received access token (no ID token)");
              
              // Store the token in sessionStorage (be careful with security)
              sessionStorage.setItem("accessToken", accessToken);
              
              // Calculate expiration time
              if (expiresIn) {
                const expiresInSeconds = parseInt(expiresIn, 10);
                const expiresAt = new Date().getTime() + expiresInSeconds * 1000;
                sessionStorage.setItem("tokenExpiresAt", expiresAt.toString());
              }
              
              // Clear the hash from the URL to prevent issues on refresh
              window.history.replaceState(null, document.title, window.location.pathname);
              
              // Force reload to update auth state
              window.location.reload();
              return;
            }
          } else {
            console.error("App: State validation failed or missing");
          }
        }
        
        // Check if we have a stored token that's not expired
        const storedToken = sessionStorage.getItem("accessToken");
        const idToken = sessionStorage.getItem("idToken");
        const tokenExpiresAt = sessionStorage.getItem("tokenExpiresAt");
        
        if ((storedToken || idToken) && tokenExpiresAt) {
          const now = new Date().getTime();
          const expiresAt = parseInt(tokenExpiresAt, 10);
          
          if (now < expiresAt) {
            console.log("App: Found valid stored token");
            // We have a valid token, continue
            setAuthChecked(true);
            return;
          } else {
            console.log("App: Stored token expired, clearing all auth data");
            // Token expired, clear all auth data
            sessionStorage.removeItem("accessToken");
            sessionStorage.removeItem("idToken");
            sessionStorage.removeItem("tokenExpiresAt");
            sessionStorage.removeItem("loginState");
            sessionStorage.removeItem("loginNonce");
            
            // Set the redirected flag to ensure we show login screen
            sessionStorage.setItem("redirectedForLogin", "true");
            
            // Clear MSAL cache
            try {
              console.log("App: Clearing MSAL cache");
              instance.clearCache();
              await instance.logout({
                onRedirectNavigate: () => false // Don't redirect yet, wait for page reload
              });
            } catch (e) {
              console.error("App: Error clearing MSAL cache:", e);
            }
            
            // Force a reload after a brief delay to ensure we go back to login page
            setTimeout(() => {
              window.location.reload();
            }, 100);
            return;
          }
        }
        
        // Now proceed with normal MSAL account check
        if (inProgress !== InteractionStatus.Login) {
          console.log("App: No login in progress, checking MSAL accounts");
          
          // Get all accounts and active account
          const allAccounts = instance.getAllAccounts();
          console.log("App: Found MSAL accounts:", allAccounts.length);
          
          // If we have accounts but no active account, set one
          if (allAccounts.length > 0) {
            if (!instance.getActiveAccount()) {
              // Set the first account as active
              console.log("App: Setting active account from MSAL:", allAccounts[0].name);
              instance.setActiveAccount(allAccounts[0]);
            } else {
              console.log("App: MSAL active account already set:", instance.getActiveAccount().name);
            }
          } else {
            console.log("App: No MSAL accounts found");
          }
        }
        
        // Mark auth check as complete
        setAuthChecked(true);
      } catch (error) {
        console.error("App: Error checking auth status:", error);
        setAuthChecked(true); // Mark as checked even on error
      }
    };
    
    checkAuthStatus();
  }, [instance, inProgress, accounts]);

  // Check if we have a manually stored token (from implicit flow)
  const isManuallyAuthenticated = () => {
    try {
      // Check for ID token first (preferred for backend)
      const idToken = sessionStorage.getItem("idToken");
      if (idToken) {
        return true;
      }
      
      // Fall back to checking access token
      const accessToken = sessionStorage.getItem("accessToken");
      const expiresAt = sessionStorage.getItem("tokenExpiresAt");
      
      if (accessToken && expiresAt) {
        const now = new Date().getTime();
        return now < parseInt(expiresAt, 10);
      }
      return false;
    } catch (e) {
      console.error("Error checking manual auth:", e);
      return false;
    }
  };
  
  return (
    <div className="container my-4">
      <div className="row">
        <div className="col-12">
          {/* Show loading while auth is being checked */}
          {!authChecked || inProgress === InteractionStatus.Login ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Verifying authentication status...</p>
            </div>
          ) : (
            // Check both MSAL auth and our manual token auth
            (accounts.length > 0 || isManuallyAuthenticated()) ? (
              // User is authenticated - show the application
              <AuthenticatedContent
                selectedFile={selectedFile}
                fileName={fileName}
                mode={mode}
                prompt={prompt}
                result={result}
                isLoading={isLoading}
                error={error}
                uploadProgress={uploadProgress}
                onVideoSelect={handleVideoSelect}
                onModeChange={setMode}
                onPromptChange={setPrompt}
                handleProcessVideo={handleProcessVideo}
                resetForm={resetForm}
              />
            ) : (
              // User is not authenticated - show login
              <Login />
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default App;